import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  useConnectedApps,
  useRevokeConnector,
} from "../../hooks/useConnectors";
import { formatDate as fmtDate } from "../../lib/format";
import i18n from "../../lib/i18n";
import { Icon } from "@/components/ui/Icon";
import { SettingsEmpty, SettingsRow, SettingsSection } from "./parts";

function formatDate(iso: string | null): string {
  if (!iso) return i18n.t("common.never");
  return fmtDate(iso, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Settings → Account → Connected apps: the account's OAuth grants (MCP clients). */
export function ConnectedAppsSettings() {
  const { t } = useTranslation();
  const { grants, loading, refetch } = useConnectedApps({ active: true });
  const revoke = useRevokeConnector();

  const [revokeId, setRevokeId] = useState<string | null>(null);

  const handleRevoke = async () => {
    if (!revokeId) return;
    await revoke.mutateAsync(revokeId);
    setRevokeId(null);
    refetch();
  };

  const target = grants.find((g) => g.id === revokeId);

  return (
    <>
      {loading ? (
        <SettingsEmpty loading />
      ) : grants.length === 0 ? (
        <SettingsEmpty icon="plug" title={t("connectedApps.empty")} hint={t("connectedApps.emptyHint")} />
      ) : (
        <SettingsSection>
          {grants.map((grant) => (
            <SettingsRow
              key={grant.id}
              icon="plug"
              label={grant.clientName}
              subtitle={
                <>
                  {grant.scope.join(", ") || t("connectedApps.defaultScope")}
                  {" · "}
                  {t("connectedApps.connected", { date: formatDate(grant.grantedAt) })}
                  {" · "}
                  {t("connectedApps.lastUsed", { date: formatDate(grant.lastUsedAt) })}
                </>
              }
              trailing={
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRevokeId(grant.id)}
                  className="text-muted hover:text-destructive"
                  title={t("connectedApps.revoke")}
                >
                  <Icon name="trash" size={14} />
                </Button>
              }
            />
          ))}
        </SettingsSection>
      )}

      <Dialog alert open={!!revokeId} onOpenChange={() => setRevokeId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("connectedApps.revokeTitle")}</DialogTitle>
          </DialogHeader>
          <div className="px-4 py-4">
            <p className="text-sm text-ink-secondary">
              <Trans
                i18nKey="connectedApps.revokeHint"
                values={{ name: target?.clientName ?? t("connectedApps.thisApp") }}
                components={{ strong: <strong className="text-ink" /> }}
              />
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRevokeId(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRevoke}
              loading={revoke.isPending}
            >
              {t("connectedApps.revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
